import type { BranchFilterItem } from "@repo/baseCloud";

import {
	BRANCHES,
	GridFiltersController,
	useFilterItemQuery as useSharedFilterItemQuery,
	useVerbiageQuery,
} from "@repo/baseCloud";
import { useMemo } from "react";

import type { LayerLookupData } from "../layers/layer";

import LocationsApi from "../../../api/locationsApi";
import { QUERY_KEYS, useFilterItemQuery } from "../../../api/queries";

export const useLocationStatuses = () =>
	useFilterItemQuery([QUERY_KEYS.Locations.Statuses, "FilterItem"], () =>
		GridFiltersController.getLocationStatuses(),
	);

export const usePartnerTypes = () =>
	useFilterItemQuery([QUERY_KEYS.Locations.PartnerTypes, "FilterItem"], () => LocationsApi.getPartnerTypes());

export const usePartners = (partnerTypeId: number | string | null | undefined) =>
	useFilterItemQuery(
		[QUERY_KEYS.Locations.Partners, partnerTypeId?.toString(), "FilterItem"],
		() => LocationsApi.getPartners({ partnerTypeId }),
		partnerTypeId,
	);

export const useUsers = () =>
	useFilterItemQuery([QUERY_KEYS.Users.Users, "FilterItem"], () => GridFiltersController.getUsers(true));

export const useBranches = () =>
	useSharedFilterItemQuery<BranchFilterItem>([BRANCHES], GridFiltersController.getBranches);

export function useLayerLookups(partnerTypeId: number | string | null | undefined): LayerLookupData {
	const [brands] = useFilterItemQuery([QUERY_KEYS.Locations.Brands, "FilterItem"], () =>
		GridFiltersController.getBrands(),
	);
	const [channels] = useFilterItemQuery([QUERY_KEYS.Locations.Channels, "FilterItem"], () =>
		GridFiltersController.getChannels(),
	);
	const [states] = useFilterItemQuery([QUERY_KEYS.Locations.States, "FilterItem"], () =>
		GridFiltersController.getStates(),
	);
	const [marketers] = useFilterItemQuery([QUERY_KEYS.Locations.Marketers, "FilterItem"], () =>
		GridFiltersController.getMarketers(),
	);
	const [companyDivisions] = useFilterItemQuery([QUERY_KEYS.Locations.CompanyDivisions, "FilterItem"], () =>
		GridFiltersController.getDivisions(),
	);
	const [companyRegions] = useFilterItemQuery([QUERY_KEYS.Locations.CompanyRegions, "FilterItem"], () =>
		GridFiltersController.getRegions(),
	);
	const [companyMarkets] = useFilterItemQuery([QUERY_KEYS.Locations.CompanyMarkets, "FilterItem"], () =>
		GridFiltersController.getMarkets(),
	);
	const [locationStatuses] = useLocationStatuses();
	const [partnerTypes] = usePartnerTypes();
	const [partners] = usePartners(partnerTypeId);
	const [users] = useUsers();
	const [offices] = useBranches();
	const { verbiage } = useVerbiageQuery();

	return useMemo(
		() => ({
			verbiage,
			partnerTypes,
			partners,
			companyDivisions,
			companyRegions,
			companyMarkets,
			locationStatuses,
			brands,
			channels,
			states,
			marketers,
			users,
			offices,
		}),
		[
			verbiage,
			partnerTypes,
			partners,
			companyDivisions,
			companyRegions,
			companyMarkets,
			locationStatuses,
			brands,
			channels,
			states,
			marketers,
			users,
			offices,
		],
	) satisfies Required<LayerLookupData>;
}
